import { z } from 'zod';
import { isValidIBAN } from 'ibantools';

export const SI_DAVCNA_RE = /^\d{8}$/;
export const SI_MATICNA_RE = /^\d{10}$/;

export const identitySchema = z.object({
  davcna: z
    .string()
    .regex(SI_DAVCNA_RE, 'Davčna številka mora vsebovati natanko 8 številk.'),
  legalName: z.string().min(2, 'Naziv mora imeti vsaj 2 znaka.'),
  tradeName: z.string().min(2, 'Ime za stranke mora imeti vsaj 2 znaka.'),
  legalForm: z.enum(['sp', 'doo', 'dd', 'kd', 'other']),
  address: z.string().min(3, 'Naslov je obvezen.'),
  city: z.string().min(2, 'Kraj je obvezen.'),
  postalCode: z.string().min(4, 'Poštna številka je obvezna.'),
  vatId: z.string().optional(),
  registrationNumber: z
    .string()
    .regex(SI_MATICNA_RE, 'Matična mora biti 10-mestna.')
    .optional()
    .or(z.literal('')),
  gdprConsent: z.literal(true, {
    errorMap: () => ({ message: 'Strinjanje s pogoji je obvezno za nadaljevanje.' }),
  }),
});

export type IdentityValues = z.infer<typeof identitySchema>;

export const bankingSchema = z.object({
  iban: z
    .string()
    .min(15, 'IBAN je prekratek.')
    .refine(
      (val) => isValidIBAN(val.replace(/\s/g, '')),
      'IBAN ni veljaven. Preverite številko.'
    ),
  paymentTermsDays: z.coerce
    .number()
    .int()
    .min(1, 'Rok plačila mora biti vsaj 1 dan.')
    .max(90, 'Rok plačila ne sme presegati 90 dni.'),
  lateInterestPct: z.coerce
    .number()
    .min(0, 'Zamudne obresti ne morejo biti negativne.')
    .max(30, 'Zamudne obresti ne smejo presegati 30 %.'),
});

export type BankingValues = z.infer<typeof bankingSchema>;

const serviceRowSchema = z.object({
  name: z.string().min(2, 'Naziv storitve je obvezen.'),
  unit: z.string().min(1, 'Enota je obvezna.'),
  priceEur: z.coerce.number().min(0, 'Cena ne sme biti negativna.'),
  vatRate: z.coerce.number().min(0).max(100),
});

export type ServiceRow = z.infer<typeof serviceRowSchema>;

export const servicesSchema = z.object({
  tradeType: z.enum([
    'hisnik', 'vodovod', 'elektrika', 'kljucavnicar',
    'mizar', 'avtomehanik', 'cistilec', 'frizer', 'drugo',
  ]),
  services: z.array(serviceRowSchema).min(1, 'Dodajte vsaj eno storitev.'),
});

export type ServicesValues = z.infer<typeof servicesSchema>;

export const voiceSchema = z.object({});
export type VoiceValues = z.infer<typeof voiceSchema>;

export function suggestVatId(davcna: string): string {
  return SI_DAVCNA_RE.test(davcna) ? `SI${davcna}` : '';
}
